import React from "react";
import ChevronLeftRoundedIcon from "@material-ui/icons/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";

const SWIPE_THRESHOLD = 45; // px of horizontal travel before it counts

const arrowClass =
    "absolute top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center " +
    "rounded-full border border-line bg-canvas-deep/70 text-ink backdrop-blur-sm " +
    "transition-all duration-300 ease-soft hover:border-accent hover:text-accent " +
    "md:flex md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100";

/**
 * Minimal crossfade carousel. Replaces react-bootstrap's Carousel so the
 * spotlight can be styled from the same token set as the rest of the site
 * (and so Bootstrap's stylesheet no longer has to ship).
 *
 * Touch devices get swipe + tappable indicators instead of the arrows, which
 * would otherwise sit on top of the caption on a narrow screen.
 */
export default function Carousel({ items, renderItem, interval = 6000 }) {
    const [index, setIndex] = React.useState(0);
    const [paused, setPaused] = React.useState(false);
    const touch = React.useRef(null);
    const count = items.length;

    const go = React.useCallback(
        next => setIndex(((next % count) + count) % count),
        [count]
    );

    React.useEffect(() => {
        if (paused || count < 2) return undefined;

        const reduced =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced) return undefined;

        const id = setInterval(() => setIndex(i => (i + 1) % count), interval);
        return () => clearInterval(id);
    }, [paused, count, interval]);

    const onKeyDown = e => {
        if (e.key === "ArrowLeft") go(index - 1);
        if (e.key === "ArrowRight") go(index + 1);
    };

    const onTouchStart = e => {
        const t = e.touches[0];
        touch.current = { x: t.clientX, y: t.clientY };
        setPaused(true);
    };

    const onTouchEnd = e => {
        const start = touch.current;
        touch.current = null;
        if (!start) return;

        const t = e.changedTouches[0];
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;

        // Ignore anything that reads as a vertical page scroll
        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
            go(dx < 0 ? index + 1 : index - 1);
        }
        setPaused(false);
    };

    return (
        <div
            role="group"
            aria-roledescription="carousel"
            aria-label="Spotlight projects"
            tabIndex={0}
            onKeyDown={onKeyDown}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            className="group relative overflow-hidden rounded-frame border border-line
                       bg-canvas-deep shadow-frame"
        >
            <div className="relative h-[420px] sm:h-[450px] lg:h-[500px]">
                {items.map((item, i) => (
                    <div
                        key={item.key}
                        aria-hidden={i !== index}
                        className={`absolute inset-0 transition-opacity duration-700 ease-soft ${
                            i === index
                                ? "opacity-100"
                                : "pointer-events-none opacity-0"
                        }`}
                    >
                        {renderItem(item, i === index)}
                    </div>
                ))}
            </div>

            {count > 1 && (
                <>
                    <button
                        type="button"
                        onClick={() => go(index - 1)}
                        aria-label="Previous project"
                        className={`${arrowClass} left-4`}
                    >
                        <ChevronLeftRoundedIcon style={{ fontSize: 22 }} />
                    </button>
                    <button
                        type="button"
                        onClick={() => go(index + 1)}
                        aria-label="Next project"
                        className={`${arrowClass} right-4`}
                    >
                        <ChevronRightRoundedIcon style={{ fontSize: 22 }} />
                    </button>

                    {/* Bars stay 3px tall but carry a 44px tap target */}
                    <div className="absolute bottom-1 right-4 z-10 flex sm:bottom-3 sm:right-6">
                        {items.map((item, i) => (
                            <button
                                key={item.key}
                                type="button"
                                onClick={() => go(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                aria-current={i === index ? "true" : undefined}
                                className="flex h-11 w-7 items-center justify-center sm:h-9 sm:w-6"
                            >
                                <span
                                    className={`h-[3px] rounded-full transition-all duration-300 ease-soft ${
                                        i === index
                                            ? "w-6 bg-accent"
                                            : "w-3 bg-white/25"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
